// Types et helpers de discussion — AUCUNE DONNÉE.
//
// Ce fichier remplace `mockChatData.ts`, qui mêlait ces types à 604 lignes de conversations
// BUNDLÉES, indexées par des identifiants de mission CODÉS EN DUR. Ces identifiants diffèrent
// d'une plateforme à l'autre : la table était donc fausse par construction ailleurs qu'en
// local, et surtout elle servait de REPLI SILENCIEUX — l'app affichait une histoire fantôme
// quand l'API ne répondait pas, ce qui a masqué plusieurs pannes réelles.
//
// Les trois helpers ci-dessous sont PURS : ils opèrent sur l'objet qu'on leur passe, d'où
// qu'il vienne. Ils survivent donc au retrait des données.

export interface ChatMessage {
  id: string;
  sender: 'tutor' | 'user';
  text: string;
  timestamp: string;
  avatar?: string;
  type?: 'text' | 'single-choice' | 'multiple-choice' | 'free-text' | 'dialogue-choice';
  options?: string[];
  correctAnswer?: string | string[];
  explanation?: string;
  /** Réaction du personnage À CHAQUE choix, indexée par le libellé de l'option (CCT-256).
   *  Sans elle, le même texte global était servi quel que soit le choix — et comme l'auteur y
   *  écrit les trois branches à la suite, le joueur qui répondait juste lisait d'abord la
   *  réfutation des options qu'il n'avait pas prises. */
  optionResponses?: Record<string, string>;
  /** Return to unasked clarification topics after each reply. Never grades a choice. */
  exploreAll?: boolean;
  /** Per-message avatar (RN `require(...)`) for multi-character JSON chats. */
  characterImage?: any;
  /** Per-message speaker name override. */
  characterName?: string;
  /**
   * A draw.io diagram this turn CARRIES (CCT-477) — read-only, in the speaker's own bubble.
   *
   * ⚠️ ON THE TURN, NOT IN A SEPARATE LIST. A diagram pinned to a message INDEX would move the
   * moment an author inserts a line; carried by the turn, the order is the author's.
   */
  diagramUrl?: string;
  /** The author's zoom for that diagram, in percent (CCT-477). */
  diagramZoom?: number;
}

/** Choix dans une discussion arborescente. */
export interface DialogueChoice {
  id: string;
  label: string;
  tutorResponse: string;
  /** false = reboucle sur le meme noeud apres la reaction du tuteur */
  advances: boolean;
  nextNodeId: string;
}

/** Noeud de discussion (tuteur + options utilisateur). */
export interface DialogueNode {
  id: string;
  tutorText: string;
  choices: DialogueChoice[];
  /** true = reponse evaluee (feedback rouge/vert). */
  isAssessment?: boolean;
  isEnd?: boolean;
}

export interface MissionConversationTree {
  startNodeId: string;
  nodes: Record<string, DialogueNode>;
}

export interface MissionChatData {
  missionId: number;
  missionTitle: string;
  tutorName: string;
  tutorAvatar: string;
  tutorColor?: string;
  tutorImage?: any;
  tutorRole: string;
  /** Script lineaire (quiz) — missions non migrees */
  messages?: ChatMessage[];
  /** Discussion a choix arborescente */
  conversationTree?: MissionConversationTree;
  unreadCount: number;
  lastActivity: string;
}

export function isTreeMission(data: MissionChatData): boolean {
  return data.conversationTree != null;
}

/** Nombre d'etapes de discussion (noeuds a choix, hors intro et outro). */
export function getTreeStepCount(tree: MissionConversationTree): number {
  return Object.values(tree.nodes).filter(
    n => !n.isEnd && n.choices.length > 0 && n.id !== tree.startNodeId,
  ).length;
}

export function getMissionChatScript(data: MissionChatData): ChatMessage[] {
  return data.messages ?? [];
}
